import { useState } from 'react';
import { collection, addDoc, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { getMilestones } from '../utils/milestoneUtils';

const COLLECTION_NAME = process.env.REACT_APP_USE_DEV_DB === 'true' ? 'runs_dev' : 'runs_2';

export const useRuns = () => {
  const [runs, setRuns] = useState([]);
  const [totalKilometers, setTotalKilometers] = useState(0);
  const [topRunners, setTopRunners] = useState([]);
  const [existingNames, setExistingNames] = useState([]);
  const [milestoneNotification, setMilestoneNotification] = useState(null);

  const fetchRuns = async () => {
    const runsCollection = collection(db, COLLECTION_NAME);
    const runsSnapshot = await getDocs(runsCollection);
    const runsList = runsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setRuns(runsList);

    const names = [...new Set(runsList.map(run => run.name))].sort();
    setExistingNames(names);

    const total = runsList.reduce((sum, run) => sum + Number(run.kilometers), 0);
    setTotalKilometers(total);

    const runnerStats = runsList.reduce((stats, run) => {
      if (!stats[run.name]) stats[run.name] = 0;
      stats[run.name] += Number(run.kilometers);
      return stats;
    }, {});

    const sortedRunners = Object.entries(runnerStats)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([name, km]) => ({
        name,
        kilometers: km,
        milestones: getMilestones(km)
      }));

    setTopRunners(sortedRunners);
  };

  const submitRun = async (runData, image) => {
    const { name, kilometers } = runData;

    const currentRunnerTotal = runs
      .filter(run => run.name === name)
      .reduce((sum, run) => sum + Number(run.kilometers), 0);

    let imageUrl = '';
    if (image) {
      if (!image.type.startsWith('image/')) {
        throw new Error('Bitte nur Bilddateien hochladen');
      }
      const storageRef = ref(storage, `run-images/${Date.now()}-${image.name}`);
      await uploadBytes(storageRef, image);
      imageUrl = await getDownloadURL(storageRef);
    }

    await addDoc(collection(db, COLLECTION_NAME), {
      ...runData,
      imageUrl,
      timestamp: new Date().toISOString()
    });

    const newTotal = currentRunnerTotal + Number(kilometers);
    const previousMilestones = getMilestones(currentRunnerTotal);
    const newMilestones = getMilestones(newTotal);

    const achievedMilestone = newMilestones.find(
      m => !previousMilestones.some(pm => pm.km === m.km)
    );

    if (achievedMilestone) {
      setMilestoneNotification({
        name,
        milestone: achievedMilestone,
        total: newTotal
      });
      setTimeout(() => setMilestoneNotification(null), 5000);
    }

    await fetchRuns();
  };

  const clearMilestoneNotification = () => setMilestoneNotification(null);

  return {
    runs,
    totalKilometers,
    topRunners,
    existingNames,
    fetchRuns,
    submitRun,
    milestoneNotification,
    clearMilestoneNotification
  };
};
